import { clsx } from 'clsx';
import { C, FARBE_NUTZER_FALLBACK } from '@/theme';
import { nutzerSymbol, nutzerSymbolTitel } from '@/lib/nutzerSymbol';

/**
 * Der Identitäts-Chip eines Mitarbeiters (A9.1).
 *
 * Runde Fläche in der Nutzerfarbe, Symbol in Cream darauf. Ohne
 * gewähltes Symbol stehen dort die Initialen — dieselben, die
 * `userKuerzel()` im Backend bildet. Es gibt bewusst keine zweite
 * Kürzel-Logik hier: das Kürzel kommt fertig aus der Antwort.
 *
 * DREI GRÖSSEN, EIN ZWECK
 *   `klein` steht an Verlaufs- und Kommunikationszeilen, wo viele davon
 *   untereinander stehen. `normal` ist die Vorgabe. `gross` steht dort,
 *   wo die Identität selbst das Thema ist — in der Nutzerwechsel-Liste.
 *
 *   Wo der Chip anklickbar ist, sorgt die aufrufende Stelle für das
 *   Berührziel von 44 Pixeln; ein Chip in einer Textzeile ist keine
 *   Schaltfläche und darf kleiner sein, sonst risse er die Zeile
 *   auseinander.
 *
 * ZWEI FORMEN, WEIL ZWEI DINGE DARIN STEHEN KÖNNEN (A14.4)
 *   Mit Symbol: gefüllte Fläche, Bildzeichen in Cream. Ein Bildzeichen
 *   ist ein grafisches Element, dafür verlangt WCAG 3:1 — gemessen
 *   erreicht die schwächste Mitarbeiterfarbe 3,37:1.
 *
 *   Mit Initialen: ein RING in der Nutzerfarbe, die Buchstaben in Ink
 *   auf dem Grund, auf dem der Chip ohnehin sitzt. Buchstaben sind Text,
 *   und dafür verlangt WCAG 4,5:1. Cream auf der Farbe erreichte je nach
 *   Mitarbeiter nur 3,44 bis 6,20 — vier der acht Farben lagen darunter,
 *   und der Rückfall-Grauton bei 3,37. Auch die bessere von Cream und
 *   Ink zu nehmen half nicht: dann blieben immer noch fünf Farben unter
 *   4,5 (die beste davon 4,07).
 *
 *   Der Ring löst das ohne eine einzige Farbe anzufassen: Ink auf Cream
 *   sind 13,7:1, und der Ring selbst ist ein grafisches Element, für das
 *   3:1 genügt — gemessen 3,01:1 im ungünstigsten Fall (Rückfall-Grau
 *   auf cream-dark).
 *
 *   Nebenbei ist das die Form, die CLAUDE.md ohnehin verlangt:
 *   Mitarbeiterfarben „immer als dezenter Akzent, nie als Fläche".
 */
type Props = {
  farbe: string | null;
  symbol: string | null;
  /** Die Initialen aus dem Backend — der Rückfall ohne Symbol. */
  kuerzel: string;
  /** Anzeigename, nur für die Vorlese-Beschriftung. */
  name: string;
  groesse?: 'klein' | 'normal' | 'gross';
  className?: string;
};

const MASSE = {
  klein: { box: 'h-5 w-5 text-[9px]', icon: 'h-3 w-3' },
  normal: { box: 'h-8 w-8 text-[11px]', icon: 'h-4 w-4' },
  gross: { box: 'h-11 w-11 text-sm', icon: 'h-6 w-6' }
} as const;

export function NutzerChip({
  farbe,
  symbol,
  kuerzel,
  name,
  groesse = 'normal',
  className
}: Props) {
  const s = nutzerSymbol(symbol);
  const mass = MASSE[groesse];
  // Ohne Farbe bliebe der Chip papierfarben und damit unsichtbar; das
  // dezente Grau ist derselbe Rückfall wie beim Farbpunkt.
  const flaeche = farbe ?? FARBE_NUTZER_FALLBACK;

  // Initialen sind Text und brauchen 4,5:1 — siehe oben. Deshalb dort
  // der Ring, sonst die gefüllte Fläche.
  const stil =
    s === null
      ? { borderColor: flaeche, color: C.ink }
      : { backgroundColor: flaeche, color: C.cream };

  return (
    <span
      className={clsx(
        'inline-flex shrink-0 items-center justify-center rounded-full font-medium leading-none',
        s === null && 'border-2',
        mass.box,
        className
      )}
      style={stil}
      title={nutzerSymbolTitel(symbol, name)}
      aria-label={nutzerSymbolTitel(symbol, name)}
    >
      {s === null ? <span aria-hidden>{kuerzel}</span> : <s.Icon aria-hidden className={mass.icon} />}
    </span>
  );
}
